import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  applyTheme,
  DARK_QUERY,
  readPreference,
  resolveTheme,
  systemPrefersDark,
  writePreference,
} from "./themeMode";
import type { ResolvedTheme, ThemePreference } from "./themeMode";

interface ThemeModeValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
}

const ThemeModeContext = createContext<ThemeModeValue | null>(null);

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [prefersDark, setPrefersDark] = useState<boolean>(systemPrefersDark);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    query.addEventListener("change", onChange);
    // The first render sampled the OS before this listener existed; catch a flip in between.
    setPrefersDark(query.matches);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const resolved = resolveTheme(preference, prefersDark);

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    writePreference(next);
    setPreferenceState(next);
  }, []);

  const value = useMemo(() => ({ preference, resolved, setPreference }), [preference, resolved, setPreference]);
  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
}

export function useThemeMode(): ThemeModeValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error("useThemeMode must be used inside ThemeModeProvider");
  return ctx;
}

/**
 * The resolved theme without requiring a provider. Inside ThemeModeProvider it follows the user's
 * choice, so canvas charts re-render on a switch. Outside one (unit tests, isolated renders) it
 * reads data-theme on <html>, falling back to light.
 */
export function useResolvedTheme(): ResolvedTheme {
  const ctx = useContext(ThemeModeContext);
  if (ctx) return ctx.resolved;
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}
